import React, { Component, createElement, Fragment } from 'react';
import { defaults, without, indexOf, map, intersection } from 'lodash';
import { List as AntList, Checkbox as AntCheckbox, Radio as AntRadio } from 'antd';
import { ListProps as AntdListProps } from 'antd/lib/list';
import { PaginationConfig } from 'antd/lib/pagination';
import { ComponentsManager } from '@comall-backend-builder/core';
import { services } from '@comall-backend-builder/core';
import { EntityRequestStatus, Entity } from '@comall-backend-builder/core/lib/parser';
import {
    CustomStyleComponentProps,
    PrivilegeComponentProps,
} from '@comall-backend-builder/components-basis/lib/component-props';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

let privilege = services.privilege;

type antdProps = Pick<
    AntdListProps<any>,
    | 'bordered'
    | 'footer'
    | 'grid'
    | 'itemLayout'
    | 'loadMore'
    | 'locale'
    | 'locale'
    | 'size'
    | 'className'
    | 'split'
    | 'pagination'
>;

interface HeaderConfig extends PrivilegeComponentProps {
    type?: string;
    component?: string;
    config?: {
        [prop: string]: any;
    };
    batchConfig?: {
        [prop: string]: any;
    };
}

interface RenderItem<T> {
    (item: T, index: number): React.ReactNode;
}

interface RowSelection extends PrivilegeComponentProps {
    type: 'radio' | 'checkbox';
}

export interface ListProps<T> extends antdProps, CustomStyleComponentProps {
    /**
     * 选择操作权限配置
     */
    rowSelection?: RowSelection;
    /**
     * 列表项key
     */
    itemKey: string;
    /**
     * 数据源
     */
    result: T[];
    /**
     * 请求状态
     */
    requestStatus: EntityRequestStatus;
    /**
     * 头部配置
     */
    header?: React.ReactNode | HeaderConfig;
    /**
     * 列表项配置
     */
    renderItem?: string | RenderItem<T>;
    /**
     * 当前页
     */
    page?: number;
    /**
     * 每页条数
     */
    perPage?: number;
    /**
     * 总条数
     */
    totalNum?: number;
    /**
     * 初始化
     */
    onInit: () => void;
    /**
     * 内容变化回调
     */
    onChange: (page: number, pageSize?: number) => void;
    /**
     * 实体
     */
    entity: Entity;
    /**
     * 参数
     */
    params?: {
        [key: string]: any;
    };
    /**
     * 是否默认加载数据
     */
    loadFirstPage: boolean;
}

interface ListState {
    indeterminate: boolean;
    selectedRowKeys: string[];
    checkAll: boolean;
    selectedItems: any[];
}
/**
 * 列表展示
 *  @param {Function | String} renderItem - 每个列表项渲染，传入函数或者动态组件名都可以
 *  @param {Object} rowSelection - 列表项选择/全选配置
 *  @param {string | ReactNode | Object} header - 列表头配置，值为object时需配置{type:'componnet',component:componnetName,batchConfig:{}}
 */
export class List extends Component<ListProps<any>, ListState> {
    // static propTypes = {
    //     renderItem: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    //     rowSelection: PropTypes.oneOfType([PropTypes.object]),
    //     header: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.object]),
    // };

    public static defaultProps = {
        itemKey: 'id',
    };

    constructor(props: ListProps<any>) {
        super(props);
        this.state = {
            indeterminate: false,
            selectedRowKeys: [],
            checkAll: false,
            selectedItems: [],
        };
        this.renderItem = this.renderItem.bind(this);
        this.renderHeader = this.renderHeader.bind(this);
        this.onCheckAllChange = this.onCheckAllChange.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentWillMount() {
        const { onInit } = this.props;
        if (onInit) {
            onInit();
        }
    }

    convertPagination(): false | PaginationConfig {
        const {
            result,
            page,
            perPage,
            onChange,
            totalNum,
            pagination: paginationConfig,
        } = this.props;
        let pagination: false | PaginationConfig;

        if (paginationConfig && result && result.length > 0) {
            pagination = {
                current: page,
                total: totalNum,
                pageSize: perPage,
                onShowSizeChange: onChange,
                onChange,
            };

            if (typeof paginationConfig === 'object') {
                defaults(pagination, paginationConfig);
            }
        } else {
            pagination = false;
        }

        return pagination;
    }

    renderItem(data: any, index: number) {
        const { renderItem } = this.props;
        if (typeof renderItem === 'function') {
            return renderItem(data, index);
        }
        return createElement(ComponentsManager.get(renderItem!), {
            ...data,
            parentProps: this.props,
        });
    }

    componentWillReceiveProps(nextProps: ListProps<any>) {
        const result = nextProps.result;
        const { itemKey } = nextProps;
        let newSelectedRowKeys = intersection(map(result, itemKey), this.state.selectedRowKeys);
        this.setState({
            selectedRowKeys: newSelectedRowKeys,
            indeterminate: !!newSelectedRowKeys.length && newSelectedRowKeys.length < result.length,
            checkAll: newSelectedRowKeys.length === result.length,
        });
    }

    isHeaderConfig(config: any): config is HeaderConfig {
        return !React.isValidElement(config);
    }

    renderHeader() {
        let { header, rowSelection, result, entity, params } = this.props;
        let headerNode = header;
        if (typeof rowSelection === 'object' && rowSelection.privilege) {
            const { path, level } = rowSelection.privilege;
            rowSelection = privilege.check(path, level) ? rowSelection : undefined;
        }
        if (typeof header === 'object' && this.isHeaderConfig(header)) {
            if (header && header.privilege) {
                const { path, level } = header.privilege;
                if (!privilege.check(path, level)) {
                    return null;
                }
            }
            if (header && header.type === 'component') {
                if (header.component === 'DataTableBatch' && !result.length) {
                    headerNode = null;
                } else {
                    headerNode = createElement(ComponentsManager.get(header.component!), {
                        ...header.config,
                        parentProps: this.props,
                        entity,
                        params,
                        selectedRowKeys: this.state.selectedRowKeys,
                        selectedItems: this.state.selectedItems,
                        batchConfig: header.batchConfig,
                    });
                }
            }
        }

        if (
            typeof header === 'undefined' &&
            (!rowSelection ||
                (rowSelection.type === 'checkbox' && !result.length) ||
                rowSelection.type === 'radio')
        ) {
            return null;
        }
        return (
            <Fragment>
                {rowSelection && rowSelection.type === 'checkbox' && !!result.length && (
                    <AntCheckbox
                        indeterminate={this.state.indeterminate}
                        onChange={this.onCheckAllChange}
                        checked={this.state.checkAll}
                    >
                        全选
                    </AntCheckbox>
                )}
                {header && <Fragment>{headerNode === null ? <div /> : headerNode}</Fragment>}
            </Fragment>
        );
    }

    onCheckAllChange(e: CheckboxChangeEvent) {
        let result = this.props.result;
        let { itemKey } = this.props;
        this.setState({
            selectedRowKeys: e.target.checked ? map(result, itemKey) : [],
            selectedItems: e.target.checked ? result : [],
            indeterminate: false,
            checkAll: e.target.checked,
        });
    }

    onChange(e: CheckboxChangeEvent, key: string, item: any) {
        let selectedRowKeys = this.state.selectedRowKeys;
        let selectedItems = this.state.selectedItems;
        let resultLength = this.props.result.length;
        let { rowSelection } = this.props;
        if (rowSelection && rowSelection.type === 'checkbox') {
            if (e.target.checked) {
                selectedRowKeys.push(key);
                selectedItems.push(item);
            } else {
                selectedRowKeys = without(selectedRowKeys, key);
                selectedItems = without(selectedItems, item);
            }
            this.setState({
                selectedRowKeys,
                selectedItems,
                indeterminate: !!selectedRowKeys.length && selectedRowKeys.length < resultLength,
                checkAll: selectedRowKeys.length === resultLength,
            });
        } else if (rowSelection && rowSelection.type === 'radio') {
            selectedRowKeys = [key];
            selectedItems = [item];
            this.setState({
                selectedRowKeys,
                selectedItems,
            });
        }
    }

    render() {
        let {
            bordered,
            footer,
            grid,
            itemLayout,
            loadMore,
            locale,
            size,
            className,
            rowSelection,
            split,
            result,
            requestStatus,
            itemKey,
        } = this.props;

        const pagination = this.convertPagination();
        if (typeof rowSelection === 'object' && rowSelection.privilege) {
            const { path, level } = rowSelection.privilege;
            rowSelection = privilege.check(path, level) ? rowSelection : undefined;
        }

        const listProps = {
            bordered,
            footer,
            grid,
            header: this.renderHeader(),
            itemLayout,
            loadMore,
            locale,
            pagination,
            size,
            loading: requestStatus === 'pending',
            className,
            split,
            dataSource: result,
        };

        return (
            <AntList
                {...listProps}
                renderItem={(item, index) => (
                    <AntList.Item
                        key={item[itemKey]}
                        className={
                            rowSelection ? 'has-list-row-selection' : 'hasnot-list-row-selection'
                        }
                    >
                        {rowSelection && rowSelection.type === 'checkbox' && (
                            <AntCheckbox
                                checked={indexOf(this.state.selectedRowKeys, item[itemKey]) > -1}
                                style={{ marginRight: 8 }}
                                onChange={(e) => {
                                    this.onChange(e, item[itemKey], item);
                                }}
                            />
                        )}
                        {rowSelection && rowSelection.type === 'radio' && (
                            <AntRadio
                                checked={indexOf(this.state.selectedRowKeys, item[itemKey]) > -1}
                                style={{ marginRight: 8 }}
                                onChange={(e) => {
                                    this.onChange(e, item[itemKey], item);
                                }}
                            />
                        )}
                        {this.renderItem(item, index)}
                    </AntList.Item>
                )}
            />
        );
    }
}
